import { Icon } from '@/shared/ui/Icon'
import { useScope } from './ScopeProvider'
import { multiCaseSummary } from './scopeUtils'

interface Props {
  onOpen?: () => void
  /** Render as a clickable chip when true (default); as plain text otherwise. */
  interactive?: boolean
  /**
   * POLISH 1B — compact composer variant. Renders a minimal filename-only
   * pill (no "Escopo:" prefix) and hides itself entirely when the scope is
   * the default "Todo o workspace", so the compact prompt bar stays
   * uncluttered. The full label is still available via the tooltip. The
   * non-compact rendering (home screen) is unchanged.
   */
  compact?: boolean
}

/**
 * Compact pill showing the active scope. Sits inside the composer footer so
 * the user always knows what context the next question will run against.
 *
 * Step 8.3 — Multi-document Case Scope: the label now reflects the number
 * of selected case documents:
 *   - 0 selected   → "Escopo: Todo o workspace"
 *   - 1 selected   → "Escopo: <filename|title>"
 *   - N selected   → "Escopo: <first> + N-1 documentos"
 */
export function ActiveScopeBadge({
  onOpen,
  interactive = true,
  compact = false,
}: Props) {
  const { selectedCaseDocuments } = useScope()
  const count = selectedCaseDocuments.length
  const summary = multiCaseSummary(selectedCaseDocuments)
  const isAll = count === 0

  // Compact composer chip: only meaningful when a specific scope is active.
  // "Todo o workspace" (the default) is already represented by the Escopo
  // tool button, so we render nothing here to keep the bar compact.
  if (compact) {
    if (isAll) return null
    const compactClass =
      'inline-flex max-w-[200px] items-center gap-1.5 rounded-full border border-violet/40 bg-violet/[0.10] px-2 py-1 text-[11px] font-medium text-text transition-[background-color,border-color,color] duration-150 ease-smooth'
    const title = `Escopo ativo: ${summary}`
    const inner = (
      <>
        <Icon name="doc" size={12} />
        <span className="min-w-0 truncate">{summary}</span>
        {count > 1 && (
          <span
            className="rounded border border-violet/40 bg-violet/[0.18] px-1 py-px font-mono text-[9.5px] text-violet"
            aria-label={`${count} documentos selecionados`}
          >
            {count}
          </span>
        )}
      </>
    )
    if (!interactive || !onOpen) {
      return (
        <span className={compactClass} data-testid="active-scope-badge" title={title}>
          {inner}
        </span>
      )
    }
    return (
      <button
        type="button"
        data-testid="active-scope-badge"
        onClick={onOpen}
        title={title}
        className={compactClass + ' hover:border-violet/60 hover:bg-violet/[0.16]'}
      >
        {inner}
      </button>
    )
  }

  const className =
    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-medium transition-[background-color,border-color,color] duration-150 ease-smooth ' +
    (isAll
      ? 'border-white/10 bg-white/[0.03] text-text-mute'
      : 'border-violet/40 bg-violet/[0.10] text-text')

  const content = (
    <>
      <Icon name={isAll ? 'grid' : 'doc'} size={12} />
      <span className="max-w-[260px] truncate">
        Escopo: <b className="font-medium">{summary}</b>
      </span>
      {count > 1 && (
        <span
          className="rounded-md border border-violet/40 bg-violet/[0.18] px-1.5 py-0.5 font-mono text-[10px] text-violet"
          aria-label={`${count} documentos selecionados`}
        >
          {count}
        </span>
      )}
    </>
  )

  if (!interactive || !onOpen) {
    return (
      <span className={className} data-testid="active-scope-badge">
        {content}
      </span>
    )
  }
  return (
    <button
      type="button"
      data-testid="active-scope-badge"
      onClick={onOpen}
      className={className + ' hover:border-violet/60 hover:bg-violet/[0.16] hover:text-text'}
      title="Alterar escopo do RAG"
    >
      {content}
    </button>
  )
}
